import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, X, Send, Loader2, Clock, CheckCircle, AlertTriangle, User, Filter } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700",
  Open: "bg-amber-100 text-amber-700",
  "In Progress": "bg-primary/15 text-primary",
  "Awaiting Customer": "bg-purple-100 text-purple-700",
  "Awaiting Internal Team": "bg-orange-100 text-orange-700",
  Resolved: "bg-green-100 text-green-700",
  Closed: "bg-secondary text-muted-foreground",
};

const PRIORITY_COLORS = {
  Low: "text-muted-foreground",
  Normal: "text-foreground",
  High: "text-amber-600 font-semibold",
  Urgent: "text-destructive font-bold",
};

const STATUSES = ["New", "Open", "In Progress", "Awaiting Customer", "Awaiting Internal Team", "Resolved", "Closed"];

export default function AdminTicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        api.get("/admin/tickets", { params: { status: filterStatus } }),
        api.get("/admin/tickets/stats"),
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/admin/tickets/${id}`);
      setSelected(data);
    } catch (err) {
      toast.error("Failed to load ticket");
    }
  };

  const sendReply = async (isInternal = false) => {
    if (!replyText.trim() || !selected) return;
    setReplying(true);
    try {
      await api.post(`/admin/tickets/${selected.id}/reply`, {
        message: replyText.trim(),
        is_internal_note: isInternal,
      });
      toast.success(isInternal ? "Internal note added" : "Reply sent to customer");
      setReplyText("");
      openTicket(selected.id);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReplying(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    try {
      await api.patch(`/admin/tickets/${selected.id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      openTicket(selected.id);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="border border-border p-4 bg-card">
            <p className="text-2xl font-display text-primary">{stats.total}</p>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">Total Tickets</p>
          </div>
          <div className="border border-border p-4 bg-card">
            <p className="text-2xl font-display text-amber-600">{(stats.by_status?.New || 0) + (stats.by_status?.Open || 0)}</p>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">Open</p>
          </div>
          <div className="border border-border p-4 bg-card">
            <p className="text-2xl font-display text-primary">{stats.by_status?.["In Progress"] || 0}</p>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">In Progress</p>
          </div>
          <div className="border border-border p-4 bg-card">
            <p className="text-2xl font-display text-green-600">{(stats.by_status?.Resolved || 0) + (stats.by_status?.Closed || 0)}</p>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mt-1">Resolved</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-muted-foreground" />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-muted px-3 py-2 text-sm outline-none"
          data-testid="ticket-filter-status"
        >
          <option value="all">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></div>
      ) : tickets.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Ticket size={32} className="mx-auto mb-3 opacity-40" />
          <p>No support tickets yet.</p>
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Ticket</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Customer</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Subject</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Priority</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground">Date</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => openTicket(t.id)}
                  className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer"
                  data-testid={`ticket-row-${t.ticket_number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-primary">{t.ticket_number}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{t.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{t.email}</p>
                  </td>
                  <td className="px-4 py-3 max-w-[200px] truncate">{t.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[t.status] || ""}`}>{t.status}</span>
                  </td>
                  <td className={`px-4 py-3 text-xs ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {new Date(t.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ticket Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="ticket-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-background h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-secondary/60 backdrop-blur px-6 py-4 flex items-center justify-between border-b border-border z-10">
              <div>
                <p className="font-display text-lg text-primary">{selected.ticket_number}</p>
                <p className="text-xs text-muted-foreground">{selected.subject}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="ticket-drawer-close"><X size={22} /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Customer</p>
                  <p className="font-medium">{selected.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{selected.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Status</p>
                  <select
                    value={selected.status}
                    onChange={(e) => updateStatus(e.target.value)}
                    className="mt-1 bg-muted px-2 py-1 text-sm outline-none w-full"
                    data-testid="ticket-status-select"
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Priority</p>
                  <p className={PRIORITY_COLORS[selected.priority]}>{selected.priority}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Category</p>
                  <p>{selected.category}</p>
                </div>
                {selected.order_number && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Order</p>
                    <p className="font-mono text-xs">{selected.order_number}</p>
                  </div>
                )}
              </div>

              {/* Conversation */}
              <div>
                <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-3">Conversation</p>
                <div className="space-y-3">
                  {(selected.conversation || []).map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-lg text-sm ${
                        msg.sender === "customer"
                          ? "bg-muted"
                          : msg.is_internal_note
                          ? "bg-amber-50 border border-amber-200"
                          : "bg-primary/5 border border-primary/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <User size={12} className={msg.sender === "customer" ? "text-muted-foreground" : "text-primary"} />
                        <span className="font-medium text-xs">{msg.sender_name}</span>
                        {msg.is_internal_note && <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Internal</span>}
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {new Date(msg.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reply */}
              <div className="border-t border-border pt-4">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary resize-none"
                  data-testid="ticket-reply-input"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => sendReply(false)}
                    disabled={replying || !replyText.trim()}
                    className="bg-primary text-primary-foreground px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                    data-testid="ticket-reply-send"
                  >
                    {replying ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Reply to Customer
                  </button>
                  <button
                    onClick={() => sendReply(true)}
                    disabled={replying || !replyText.trim()}
                    className="border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                    data-testid="ticket-reply-internal"
                  >
                    Add Internal Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
